import { Bot, Check, Clock, Flame, Sparkles, Trash2, User, UserCog, X } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ClaimButton } from "@/components/messages/claim-button";
import { getAvatarColorClasses } from "@/lib/messages/avatar-color";
import { withAppTimeZone } from "@/lib/timezone";
import { formatWaitShort, getThreadInitials } from "@/lib/messages/messages-helpers";
import { humanizeLockReason } from "@/lib/thread-flags";
import { getAuthUser } from "@/lib/api/token-store";
import { cn } from "@/lib/utils";

function taskOwnerInitials(name) {
  const tokens = String(name || "")
    .split(/\s+/)
    .filter(Boolean);
  if (tokens.length === 0) return "?";
  const first = tokens[0][0]?.toUpperCase() || "";
  if (tokens.length === 1) return first;
  return first + (tokens[tokens.length - 1][0]?.toUpperCase() || "");
}

/** Source tag for a task card: where the task came from (design 02 / 05). */
export function TaskSourceTag({ task, className }) {
  const me = String(getAuthUser()?.id || "").trim();
  let Icon = User;
  let label;
  let toneClassName = "bg-muted text-muted-foreground";
  if (task.source === "auto") {
    Icon = Sparkles;
    // Origins embed machine codes ("lock out_of_service", "staff_handles") — humanize for display.
    label = task.origin ? `Auto · ${humanizeLockReason(task.origin)}` : "Auto";
    toneClassName = "bg-violet-50 text-violet-600 dark:bg-violet-950/60 dark:text-violet-300";
  } else if (task.source === "ai") {
    Icon = Bot;
    label = task.origin ? `AI · ${humanizeLockReason(task.origin)}` : "AI concierge";
    toneClassName = "bg-teal-50 text-teal-600 dark:bg-teal-950/60 dark:text-teal-300";
  } else {
    const mine = Boolean(me && task.createdByUserId === me);
    const createdByName = String(task.createdByName || "").trim();
    label = mine ? "You added" : createdByName ? `Added by ${createdByName}` : "Added by staff";
  }
  return (
    <span
      className={cn(
        "inline-flex max-w-full items-center gap-1 rounded-[5px] px-1.5 py-[2px] text-[9.5px] font-semibold leading-none",
        toneClassName,
        className,
      )}
      title={label}
    >
      <Icon className="h-2.5 w-2.5 shrink-0" strokeWidth={2.5} aria-hidden />
      <span className="truncate">{label}</span>
    </span>
  );
}

/** Due ("Due 2:00 PM", amber) or overdue ("Overdue 15h", rose) chip for an open task. */
export function TaskDueChip({ task, nowMs = Date.now(), className }) {
  if (!task?.dueAt || task.status === "done") return null;
  const due = new Date(task.dueAt).getTime();
  if (Number.isNaN(due)) return null;
  if (due <= nowMs) {
    const overdueMinutes = Math.max(1, Math.floor((nowMs - due) / 60000));
    return (
      <span
        className={cn(
          "inline-flex shrink-0 items-center gap-1 rounded-[5px] bg-rose-50 px-1.5 py-[2px] text-[9.5px] font-bold leading-none text-rose-600 dark:bg-rose-950/60 dark:text-rose-300",
          className,
        )}
        title={`Overdue since ${new Date(due).toLocaleString(undefined, withAppTimeZone())}`}
      >
        <Flame className="h-2.5 w-2.5 shrink-0" strokeWidth={2.5} aria-hidden />
        Overdue {formatWaitShort(overdueMinutes)}
      </span>
    );
  }
  const timeLabel = new Date(due).toLocaleTimeString(undefined, withAppTimeZone({ hour: "numeric", minute: "2-digit" }));
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-[5px] bg-amber-50 px-1.5 py-[2px] text-[9.5px] font-semibold leading-none text-amber-700 dark:bg-amber-950/60 dark:text-amber-300",
        className,
      )}
      title={`Due ${new Date(due).toLocaleString(undefined, withAppTimeZone())}`}
    >
      <Clock className="h-2.5 w-2.5 shrink-0" strokeWidth={2.5} aria-hidden />
      Due {timeLabel}
    </span>
  );
}

/**
 * The "Assign to…" picker for a task: thread team members plus a clear option. The trigger
 * is the owner avatar (claimed) or a small UserCog button (unclaimed).
 */
function TaskAssignPopover({ task, assignableUsers, onAssign, children }) {
  return (
    <Popover>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-1.5">
        <div className="px-1.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Assign to
        </div>
        <ScrollArea viewportClassName="max-h-56">
          {assignableUsers.length === 0 ? (
            <p className="px-1.5 py-2 text-xs text-muted-foreground">No team members on this thread.</p>
          ) : null}
          {assignableUsers.map((member) => {
            const active = member.id === task.assigneeId;
            return (
              <button
                key={member.key || member.id}
                type="button"
                onClick={() => {
                  if (!active) onAssign(task.id, member.id, member.label);
                }}
                className={cn(
                  "flex w-full items-center gap-2 rounded-md px-1.5 py-1.5 text-left text-xs transition hover:bg-muted/70",
                  active ? "bg-muted" : "",
                )}
              >
                <Avatar className="h-5 w-5">
                  <AvatarImage src={member.avatarUrl} />
                  <AvatarFallback
                    className={cn("text-[9px] font-semibold", getAvatarColorClasses(member.label || member.id))}
                  >
                    {getThreadInitials(member.label || member.id)}
                  </AvatarFallback>
                </Avatar>
                <span className="min-w-0 flex-1 truncate">{member.label}</span>
                {active ? <span className="h-2 w-2 shrink-0 rounded-full bg-indigo-500" aria-hidden /> : null}
              </button>
            );
          })}
        </ScrollArea>
        {task.assigneeId ? (
          <button
            type="button"
            onClick={() => onAssign(task.id, "")}
            className="mt-1 flex w-full items-center gap-1.5 rounded-md px-1.5 py-1.5 text-left text-xs text-muted-foreground transition hover:bg-muted/70"
          >
            <X className="h-3.5 w-3.5 shrink-0" aria-hidden /> Clear assignee
          </button>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

/**
 * One checklist task card (design 02-thread-tasks): round checkbox, title, source tag,
 * due/overdue chip, owner avatar — or a compact Claim button while the task is unclaimed
 * (pass `onClaim`). With `onAssign` + `assignableUsers` the owner avatar (or a small UserCog
 * button) opens an assign-to-teammate picker. Done cards dim and strike through; a subtle
 * hover trash removes a task.
 */
export function TaskCard({
  task,
  nowMs = Date.now(),
  assignableUsers = [],
  onToggle,
  onClaim,
  onAssign,
  onDelete,
}) {
  const me = String(getAuthUser()?.id || "").trim();
  const done = task.status === "done";
  const overdue =
    !done && task.dueAt ? new Date(task.dueAt).getTime() <= nowMs : false;
  const assigneeName = String(task.assignee?.displayName || "").trim();
  const assigneeIsMe = Boolean(task.assigneeId && me && task.assigneeId === me);
  // An ASSIGNED task can always open the menu (clearing needs no candidates); offering the
  // picker on an unclaimed task only makes sense with someone to pick.
  const canPickAssignee = Boolean(!done && onAssign);
  const canOfferAssignees = Boolean(canPickAssignee && assignableUsers.length > 0);
  const doneTime = task.doneAt
    ? new Date(task.doneAt).toLocaleString(undefined, withAppTimeZone({
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }))
    : "";

  return (
    <div
      className={cn(
        "group/task rounded-lg border px-3 py-2.5 transition",
        done
          ? "border-border bg-muted/50 opacity-80"
          : overdue || task.source === "auto"
            ? "border-indigo-200 bg-card shadow-sm dark:border-indigo-500/30"
            : "border-border bg-card",
      )}
    >
      <div className="flex items-start gap-2.5">
        <button
          type="button"
          role="checkbox"
          aria-checked={done}
          aria-label={done ? `Reopen task: ${task.title}` : `Complete task: ${task.title}`}
          onClick={() => onToggle?.(task.id, !done)}
          className={cn(
            "mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            done
              ? "bg-emerald-500 text-white dark:bg-emerald-500"
              : cn(
                  "border-2 hover:border-emerald-500",
                  overdue || task.source === "auto"
                    ? "border-indigo-400 dark:border-indigo-500"
                    : "border-muted-foreground/40",
                ),
          )}
        >
          {done ? <Check className="h-2.5 w-2.5" strokeWidth={3} aria-hidden /> : null}
        </button>
        <div className="min-w-0 flex-1">
          <p
            className={cn(
              "text-xs leading-snug",
              done ? "text-muted-foreground line-through" : "font-medium text-foreground",
            )}
          >
            {task.title}
          </p>
          {done ? (
            <p className="mt-0.5 text-[9.5px] text-muted-foreground">
              {task.doneByName ? `${task.doneByName} · ` : ""}done{doneTime ? ` ${doneTime}` : ""}
            </p>
          ) : (
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <TaskSourceTag task={task} />
              <TaskDueChip task={task} nowMs={nowMs} />
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {!done && !task.assigneeId && onClaim ? (
            <ClaimButton
              onClick={() => onClaim(task.id)}
              className="px-1.5 py-[2px] text-[10px]"
              title="Claim this task — it's yours"
            />
          ) : null}
          {!done && !task.assigneeId && canOfferAssignees ? (
            <TaskAssignPopover task={task} assignableUsers={assignableUsers} onAssign={onAssign}>
              <button
                type="button"
                aria-label="Assign this task to a teammate"
                title="Assign this task to a teammate"
                className="inline-flex h-[18px] w-[18px] items-center justify-center rounded-md text-muted-foreground/60 transition hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <UserCog className="size-3!" aria-hidden />
              </button>
            </TaskAssignPopover>
          ) : null}
          {!done && task.assigneeId ? (
            canPickAssignee ? (
              <TaskAssignPopover task={task} assignableUsers={assignableUsers} onAssign={onAssign}>
                <button
                  type="button"
                  aria-label={assigneeIsMe ? "Claimed by you — reassign" : `Claimed by ${assigneeName} — reassign`}
                  title={assigneeIsMe ? "Claimed by you — click to reassign" : `Claimed by ${assigneeName || "a teammate"} — click to reassign`}
                  className={cn(
                    "inline-flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-bold leading-none transition hover:ring-2 hover:ring-ring/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    assigneeIsMe
                      ? "bg-indigo-600 text-white dark:bg-indigo-500"
                      : "bg-muted text-muted-foreground",
                  )}
                >
                  {taskOwnerInitials(assigneeIsMe && !assigneeName ? "Me" : assigneeName)}
                </button>
              </TaskAssignPopover>
            ) : assigneeName || assigneeIsMe ? (
              <span
                className={cn(
                  "inline-flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-bold leading-none",
                  assigneeIsMe
                    ? "bg-indigo-600 text-white dark:bg-indigo-500"
                    : "bg-muted text-muted-foreground",
                )}
                title={assigneeIsMe ? "Claimed by you" : `Claimed by ${assigneeName}`}
              >
                {taskOwnerInitials(assigneeIsMe && !assigneeName ? "Me" : assigneeName)}
              </span>
            ) : null
          ) : null}
          {onDelete ? (
            <button
              type="button"
              aria-label={`Delete task: ${task.title}`}
              onClick={() => onDelete(task.id)}
              className="inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground/0 transition hover:bg-muted hover:text-destructive group-hover/task:text-muted-foreground/70 focus-visible:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Trash2 className="h-3 w-3" aria-hidden />
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
